// Pure-function snippet generator for the /integrations page.
//
// The goal of this module is to keep all "render a copy-paste-able code
// sample" logic dependency-free so it can be unit-tested in isolation and
// re-used elsewhere if needed. There is intentionally NO React, NO fetch,
// NO DOM access here — every helper takes plain data and returns a string.
//
// Three protocols are supported, mirroring the gateway's request surface:
//
//   * OpenAI Chat Completions     -> POST /v1/chat/completions
//   * OpenAI Responses            -> POST /v1/responses
//   * Anthropic Messages          -> POST /v1/messages
//
// For each protocol, snippets are generated for three client languages:
// curl, Python (openai / anthropic SDK), and TypeScript (fetch).
//
// All snippets share the same properties:
//
//   * Secret-safe: the API key is referenced via a language-idiomatic
//     placeholder such as ${GATEWAY_API_KEY} or os.environ["..."]. The
//     generator never embeds an actual key, and the "mask" parameter exists
//     only as documentation about how secrets are handled downstream — it is
//     never substituted into the snippet.
//   * Deterministic: same inputs always produce the same string.
//   * Stable ordering: model list and message arrays are emitted in the
//     caller's order — the generator never re-sorts.
//
// What this module does NOT do:
//
//   * It does NOT call the network or read configuration. Callers must
//     supply model IDs, base URL, and the env var name holding the key.
//   * It does NOT pick a default protocol or language. The UI layer is
//     responsible for that — keep this module small and predictable.

export type ProtocolId = 'openai-chat' | 'openai-responses' | 'anthropic-messages'
export type LanguageId = 'curl' | 'python' | 'typescript'

export interface ProtocolMeta {
  id: ProtocolId
  label: string
  path: string
  description: string
}

export interface LanguageMeta {
  id: LanguageId
  label: string
}

export const PROTOCOLS: readonly ProtocolMeta[] = [
  {
    id: 'openai-chat',
    label: 'OpenAI Chat Completions',
    path: '/v1/chat/completions',
    description: 'OpenAI-compatible /v1/chat/completions — the canonical gateway endpoint used by every SDK.',
  },
  {
    id: 'openai-responses',
    label: 'OpenAI Responses',
    path: '/v1/responses',
    description: 'OpenAI Responses API compatibility layer — used by the latest OpenAI SDKs and agent frameworks.',
  },
  {
    id: 'anthropic-messages',
    label: 'Anthropic Messages',
    path: '/v1/messages',
    description: 'Anthropic-compatible /v1/messages — drop-in for @anthropic-ai/sdk and the Claude ecosystem.',
  },
] as const

export const LANGUAGES: readonly LanguageMeta[] = [
  { id: 'curl', label: 'curl' },
  { id: 'python', label: 'Python' },
  { id: 'typescript', label: 'TypeScript' },
] as const

/** Canonical env-var name used in every snippet to reference the API key. */
export const DEFAULT_KEY_ENV_VAR = 'GATEWAY_API_KEY'

/** Canonical env-var name used in every snippet to reference the base URL. */
export const DEFAULT_BASE_URL_ENV_VAR = 'GATEWAY_BASE_URL'

/** Default base URL placeholder shown when the gateway is not configured. */
export const DEFAULT_BASE_URL = 'https://llm-gateway.overlink.top'

/** Default sample user prompt embedded in every snippet. */
export const DEFAULT_SAMPLE_PROMPT = 'Say hello in one short sentence.'

export interface SnippetInputs {
  /** Protocol the snippet targets. */
  protocol: ProtocolId
  /** Language the snippet is rendered in. */
  language: LanguageId
  /** Fully-qualified model id (e.g. `z-ai/glm-5.2`). */
  model: string
  /** Gateway origin (no trailing slash, no /v1 suffix). */
  baseUrl: string
  /** Env var name holding the API key. Never substituted into the snippet. */
  keyEnvVar: string
  /** Env var name holding the base URL. */
  baseUrlEnvVar: string
  /** Sample prompt rendered as the user message. */
  prompt: string
  /** Cap on tokens generated by the upstream model. */
  maxTokens: number
}

const DEFAULT_INPUTS: Omit<SnippetInputs, 'protocol' | 'language'> = {
  model: 'z-ai/glm-5.2',
  baseUrl: DEFAULT_BASE_URL,
  keyEnvVar: DEFAULT_KEY_ENV_VAR,
  baseUrlEnvVar: DEFAULT_BASE_URL_ENV_VAR,
  prompt: DEFAULT_SAMPLE_PROMPT,
  maxTokens: 256,
}

export function resolveSnippetInputs(
  partial: Partial<SnippetInputs> & Pick<SnippetInputs, 'protocol' | 'language'>,
): SnippetInputs {
  return {
    ...DEFAULT_INPUTS,
    ...partial,
  }
}

/**
 * Render a single snippet for the given protocol+language. The output is
 * plain text and never contains a real API key.
 */
export function renderSnippet(input: SnippetInputs): string {
  switch (input.language) {
    case 'curl':
      return renderCurl(input)
    case 'python':
      return renderPython(input)
    case 'typescript':
      return renderTypeScript(input)
    default: {
      const _exhaustive: never = input.language
      throw new Error(`unsupported language: ${String(_exhaustive)}`)
    }
  }
}

function quoteForPython(value: string): string {
  // Triple-quoted Python string — escape backslashes and embedded triple quotes.
  return value.replace(/\\/g, '\\\\').replace(/"""/g, '\\"\\"\\"').replace(/\r?\n/g, '\\n')
}

function quoteForTypeScript(value: string): string {
  // Template literal — escape backslashes, backticks, and ${...}.
  return value
    .replace(/\\/g, '\\\\')
    .replace(/`/g, '\\`')
    .replace(/\$\{/g, '\\${')
    .replace(/\r?\n/g, '\\n')
}

function renderCurl(input: SnippetInputs): string {
  const envVar = input.keyEnvVar
  const baseVar = input.baseUrlEnvVar
  switch (input.protocol) {
    case 'openai-chat':
      return [
        `# Send a single chat completion to the gateway.`,
        `# The API key is read from the shell environment so it never appears in your script.`,
        `BASE_URL="\${${baseVar}:-${input.baseUrl}}"`,
        `curl -fsS "\${BASE_URL}/v1/chat/completions" \\`,
        `  -H "Authorization: Bearer \${${envVar}}" \\`,
        `  -H 'content-type: application/json' \\`,
        `  -d '${JSON.stringify({
          model: input.model,
          messages: [{ role: 'user', content: input.prompt }],
          stream: false,
          max_tokens: input.maxTokens,
        })}'`,
      ].join('\n')
    case 'openai-responses':
      return [
        `# Send a single Responses API request to the gateway.`,
        `BASE_URL="\${${baseVar}:-${input.baseUrl}}"`,
        `curl -fsS "\${BASE_URL}/v1/responses" \\`,
        `  -H "Authorization: Bearer \${${envVar}}" \\`,
        `  -H 'content-type: application/json' \\`,
        `  -d '${JSON.stringify({
          model: input.model,
          input: input.prompt,
          max_output_tokens: input.maxTokens,
        })}'`,
      ].join('\n')
    case 'anthropic-messages':
      return [
        `# Send a single Anthropic Messages request to the gateway.`,
        `# Anthropic uses x-api-key + anthropic-version headers instead of Bearer.`,
        `BASE_URL="\${${baseVar}:-${input.baseUrl}}"`,
        `curl -fsS "\${BASE_URL}/v1/messages" \\`,
        `  -H "x-api-key: \${${envVar}}" \\`,
        `  -H 'anthropic-version: 2023-06-01' \\`,
        `  -H 'content-type: application/json' \\`,
        `  -d '${JSON.stringify({
          model: input.model,
          max_tokens: input.maxTokens,
          messages: [{ role: 'user', content: input.prompt }],
        })}'`,
      ].join('\n')
    default: {
      const _exhaustive: never = input.protocol
      throw new Error(`unsupported protocol: ${String(_exhaustive)}`)
    }
  }
}

function renderPython(input: SnippetInputs): string {
  const envVar = input.keyEnvVar
  const baseVar = input.baseUrlEnvVar
  const promptPy = quoteForPython(input.prompt)
  switch (input.protocol) {
    case 'openai-chat':
      return [
        `# pip install openai>=1.0`,
        `import os`,
        `from openai import OpenAI`,
        ``,
        `# API key is read from the environment so it never lives in source control.`,
        `client = OpenAI(`,
        `    api_key=os.environ["${envVar}"],`,
        `    base_url=os.environ.get("${baseVar}", "${input.baseUrl}") + "/v1",`,
        `)`,
        ``,
        `response = client.chat.completions.create(`,
        `    model="${input.model}",`,
        `    messages=[{"role": "user", "content": """${promptPy}"""}],`,
        `    max_tokens=${input.maxTokens},`,
        `)`,
        `print(response.choices[0].message.content)`,
      ].join('\n')
    case 'openai-responses':
      return [
        `# pip install openai>=1.40 (Responses API support)`,
        `import os`,
        `from openai import OpenAI`,
        ``,
        `client = OpenAI(`,
        `    api_key=os.environ["${envVar}"],`,
        `    base_url=os.environ.get("${baseVar}", "${input.baseUrl}") + "/v1",`,
        `)`,
        ``,
        `response = client.responses.create(`,
        `    model="${input.model}",`,
        `    input="""${promptPy}""",`,
        `    max_output_tokens=${input.maxTokens},`,
        `)`,
        `print(response.output_text)`,
      ].join('\n')
    case 'anthropic-messages':
      return [
        `# pip install anthropic>=0.30`,
        `import os`,
        `from anthropic import Anthropic`,
        ``,
        `client = Anthropic(`,
        `    api_key=os.environ["${envVar}"],`,
        `    base_url=os.environ.get("${baseVar}", "${input.baseUrl}"),`,
        `)`,
        ``,
        `message = client.messages.create(`,
        `    model="${input.model}",`,
        `    max_tokens=${input.maxTokens},`,
        `    messages=[{"role": "user", "content": """${promptPy}"""}],`,
        `)`,
        `print(message.content[0].text)`,
      ].join('\n')
    default: {
      const _exhaustive: never = input.protocol
      throw new Error(`unsupported protocol: ${String(_exhaustive)}`)
    }
  }
}

function renderTypeScript(input: SnippetInputs): string {
  const envVar = input.keyEnvVar
  const baseVar = input.baseUrlEnvVar
  const promptTs = quoteForTypeScript(input.prompt)
  switch (input.protocol) {
    case 'openai-chat':
      return [
        `// Run with: tsx integrations.ts  (no external deps)`,
        `const apiKey = process.env.${envVar}`,
        `const baseUrl = process.env.${baseVar} ?? "${input.baseUrl}"`,
        `if (!apiKey) throw new Error("${envVar} is not set")`,
        ``,
        `const response = await fetch(\`\${baseUrl}/v1/chat/completions\`, {`,
        `  method: "POST",`,
        `  headers: {`,
        `    "Authorization": \`Bearer \${apiKey}\`,`,
        `    "content-type": "application/json",`,
        `  },`,
        `  body: JSON.stringify({`,
        `    model: "${input.model}",`,
        `    messages: [{ role: "user", content: \`${promptTs}\` }],`,
        `    max_tokens: ${input.maxTokens},`,
        `  }),`,
        `})`,
        `if (!response.ok) throw new Error(\`HTTP \${response.status}\`)`,
        `const data = await response.json() as { choices: Array<{ message: { content: string } }> }`,
        `console.log(data.choices[0]?.message.content)`,
      ].join('\n')
    case 'openai-responses':
      return [
        `// Run with: tsx integrations.ts  (no external deps)`,
        `const apiKey = process.env.${envVar}`,
        `const baseUrl = process.env.${baseVar} ?? "${input.baseUrl}"`,
        `if (!apiKey) throw new Error("${envVar} is not set")`,
        ``,
        `const response = await fetch(\`\${baseUrl}/v1/responses\`, {`,
        `  method: "POST",`,
        `  headers: {`,
        `    "Authorization": \`Bearer \${apiKey}\`,`,
        `    "content-type": "application/json",`,
        `  },`,
        `  body: JSON.stringify({`,
        `    model: "${input.model}",`,
        `    input: \`${promptTs}\`,`,
        `    max_output_tokens: ${input.maxTokens},`,
        `  }),`,
        `})`,
        `if (!response.ok) throw new Error(\`HTTP \${response.status}\`)`,
        `const data = await response.json() as { output_text?: string }`,
        `console.log(data.output_text)`,
      ].join('\n')
    case 'anthropic-messages':
      return [
        `// Run with: tsx integrations.ts  (no external deps)`,
        `const apiKey = process.env.${envVar}`,
        `const baseUrl = process.env.${baseVar} ?? "${input.baseUrl}"`,
        `if (!apiKey) throw new Error("${envVar} is not set")`,
        ``,
        `const response = await fetch(\`\${baseUrl}/v1/messages\`, {`,
        `  method: "POST",`,
        `  headers: {`,
        `    "x-api-key": apiKey,`,
        `    "anthropic-version": "2023-06-01",`,
        `    "content-type": "application/json",`,
        `  },`,
        `  body: JSON.stringify({`,
        `    model: "${input.model}",`,
        `    max_tokens: ${input.maxTokens},`,
        `    messages: [{ role: "user", content: \`${promptTs}\` }],`,
        `  }),`,
        `})`,
        `if (!response.ok) throw new Error(\`HTTP \${response.status}\`)`,
        `const data = await response.json() as { content: Array<{ text: string }> }`,
        `console.log(data.content[0]?.text)`,
      ].join('\n')
    default: {
      const _exhaustive: never = input.protocol
      throw new Error(`unsupported protocol: ${String(_exhaustive)}`)
    }
  }
}

/**
 * Build a `.env.example` file documenting the variables the snippet suite
 * references. The file is safe to commit — every value is a placeholder and
 * no real credential is embedded.
 */
export function renderEnvExample(input: Pick<SnippetInputs, 'baseUrl' | 'keyEnvVar' | 'baseUrlEnvVar'>): string {
  return [
    `# Drop into .env (do NOT commit the populated file).`,
    `# The integrations page reads these variables; never paste a real key here.`,
    ``,
    `# Gateway origin. This is the public Worker origin; the dashboard itself`,
    `# uses a same-origin /api proxy, but external clients connect here directly.`,
    `# outside the dashboard needs the public origin directly.`,
    `${input.baseUrlEnvVar}=${input.baseUrl}`,
    ``,
    `# Long-lived API key for the gateway. Issue one in the API Keys page`,
    `# (the gateway returns the secret exactly once at creation time).`,
    `${input.keyEnvVar}=replace-with-the-key-issued-by-api-keys-page`,
    ``,
  ].join('\n')
}

/** Convenience predicate used by the UI to know whether a snippet is empty. */
export function isSnippetEmpty(value: string): boolean {
  return value.trim().length === 0
}